import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  posthogTrackEvent,
  IntegrationEvents as PostHogIntegrationEvents,
} from "@/assets/posthog";
import { useApi, apiResolver } from "../useApi";
import {
  ConnectIntegrationRequest,
  CreateIntegrationRequest,
  Integration,
  TenantGroup,
  isIntegrationName,
  CreateBrandsConfigurationRequest,
  KnowledgeJob,
  ArticleCategory,
  ZendeskConfigurationRequest,
  GetBrandsConfigurationRequest,
  ConfluenceConfigurationRequest,
  ZendeskTicketGroupsResponse,
  UpsertTenantGroupDto,
  CreateSmoochIntegrationRequest,
  UpdateIntegrationMetadataRequest,
} from "./Integration.model";
import { AxiosInstance } from "axios";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useCallback } from "react";
import Nango from "@nangohq/frontend";
import { toast } from "sonner";

import { integrationNameToGalleryItem } from "./integrationNameToGalleryItem";

export function useIntegrationApi(api: AxiosInstance) {
  return {
    deleteIntegration: async (integrationId: string) => {
      const data = apiResolver<void>(
        api.post(`/platform/integration/${integrationId}/delete`)
      );
      return data;
    },
    getIntegrations: async () => {
      const data = await apiResolver<Integration[]>(
        api.get(`/platform/integration`)
      );

      return data.filter((integration) => isIntegrationName(integration.name));
    },
    createIntegration: async (request: ConnectIntegrationRequest) => {
      const data = apiResolver<void>(
        api.post(`/platform/integration`, request)
      );
      posthogTrackEvent({
        event: PostHogIntegrationEvents.CREATE_INTEGRATION,
        properties: {
          integrationName: request.name,
          integrationType: request.type,
          timestamp: new Date().toISOString(),
        },
      });

      return data;
    },
    completeTicketSystemConfiguration: async (
      request: CreateIntegrationRequest
    ) => {
      const data = apiResolver<void>(
        api.post(`/platform/integration/ticket-system/complete`, request)
      );

      posthogTrackEvent({
        event: PostHogIntegrationEvents.COMPLETE_TICKET_SYSTEM_INTEGRATION,
        properties: {
          integrationName: request.name,
          integrationType: request.type,
          timestamp: new Date().toISOString(),
        },
      });

      return data;
    },
    getTenantGroupsInTicketSystem: async () => {
      return apiResolver<{ groups: TenantGroup[] }>(
        api.get(`/platform/integration/groups`)
      );
    },
    createBrandsConfiguration: async (
      request: CreateBrandsConfigurationRequest
    ) => {
      const data = apiResolver<void>(
        api.post(`/platform/integration/configuration/brands`, request)
      );

      return data;
    },
    getBrandsConfiguration: async () => {
      const data = apiResolver<GetBrandsConfigurationRequest>(
        api.get(`/platform/integration/configuration/brands`)
      );

      return data;
    },
    getJobs: async () => {
      return apiResolver<KnowledgeJob[]>(
        api.get(`/knowledge-base-handler/jobs`)
      );
    },
    getArticleCategories: async (source: string, brand?: string) => {
      return apiResolver<ArticleCategory[]>(
        api.get(
          `/knowledge-base-handler/article-categories/sources/${source}/categories${
            brand ? `?brand=${brand}` : ""
          }`
        )
      );
    },
    getSelectedCategories: async (source: string, brand?: string) => {
      return apiResolver<ArticleCategory[]>(
        api.get(
          `/knowledge-base-handler/article-categories/sources/${source}/categories/selected${
            brand ? `?brand=${brand}` : ""
          }`
        )
      );
    },
    updateSelectedArticleCategories: async (
      request: ZendeskConfigurationRequest
    ) => {
      return apiResolver<void>(
        api.post(`/knowledge-base-handler/article-categories`, request)
      );
    },
    updateConfluenceConfiguration: async (
      request: ConfluenceConfigurationRequest
    ) => {
      return apiResolver<void>(
        api.post(`/knowledge-base-handler/confluence/configuration`, request)
      );
    },
    getConfluenceConfiguration: async () => {
      return apiResolver<ConfluenceConfigurationRequest>(
        api.get(`/knowledge-base-handler/confluence/configuration`)
      );
    },
    getZendeskTicketGroups: async () => {
      return apiResolver<ZendeskTicketGroupsResponse>(
        api.get(`/platform/groups`)
      );
    },
    updateZendeskTicketGroups: async (
      groups: Array<{
        externalId: string;
        name: string;
        isIncludePolicy: boolean;
      }>,
      tenantId: string
    ) => {
      // Since we have individual PUT endpoints, we need to make multiple calls
      const promises = groups.map((group) => {
        const url = `/platform/groups/tenants/${tenantId}/groups/${group.externalId}`;
        const payload: UpsertTenantGroupDto = {
          tenantId,
          externalId: group.externalId,
          name: group.name,
          isIncludePolicy: group.isIncludePolicy,
        };

        console.log(
          `Making PUT request to ${
            group.isIncludePolicy ? "include" : "exclude"
          } group:`,
          url,
          "with data:",
          payload
        );

        // Always use PUT, let the backend handle include/exclude based on isIncludePolicy
        return apiResolver<void>(api.put(url, payload));
      });

      const results = await Promise.all(promises);
      console.log("Update results:", results);
      return results;
    },
    createSmoochIntegration: async (
      request: CreateSmoochIntegrationRequest
    ) => {
      const data = apiResolver<void>(
        api.post(`/sessions-api/integrations`, request)
      );

      return data;
    },
    updateIntegrationMetadata: async (
      request: UpdateIntegrationMetadataRequest
    ) => {
      const data = apiResolver<void>(
        api.put(`/platform/integration/metadata`, request)
      );

      return data;
    },
    syncKnowledgeAndWiki: async () => {
      const data = apiResolver<void>(
        api.post(`/knowledge-base-handler/knowledge/sync`)
      );

      return data;
    },
  };
}

export const useDeleteIntegration = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const { deleteIntegration } = useIntegrationApi(api);

  return useMutation({
    mutationFn: (integrationId: string) => deleteIntegration(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries(["getTenantIntegrations"]);
    },
  });
};

export const useIntegrations = () => {
  const { api } = useApi();
  const { getIntegrations } = useIntegrationApi(api);

  return useQuery<Integration[]>({
    queryKey: "getTenantIntegrations",
    queryFn: getIntegrations,
  });
};

export const useGetJobs = () => {
  const { api } = useApi();
  const { getJobs } = useIntegrationApi(api);

  return useQuery<KnowledgeJob[]>({
    queryKey: "getJobs",
    queryFn: getJobs,
  });
};

export const useGetArticleCategories = (source: string) => {
  const { api } = useApi();
  const { getArticleCategories: getArticleCategories } = useIntegrationApi(api);

  return useQuery<ArticleCategory[]>({
    queryKey: ["getArticleCategories", source],
    queryFn: () => getArticleCategories(source),
  });
};

export const useGetSelectedCategories = (source: string) => {
  const { api } = useApi();
  const { getSelectedCategories: getSelectedCategories } =
    useIntegrationApi(api);

  return useQuery<ArticleCategory[]>({
    queryKey: ["getSelectedCategories", source],
    queryFn: () => getSelectedCategories(source),
  });
};

export const useGetTenantGroupsInTicketSystem = () => {
  const { api } = useApi();
  const { getTenantGroupsInTicketSystem } = useIntegrationApi(api);

  return useQuery<{ groups: TenantGroup[] }>({
    queryKey: "getTenantGroupsInTicketSystem",
    queryFn: getTenantGroupsInTicketSystem,
  });
};

export const useGetZendeskTicketGroups = () => {
  const { api } = useApi();
  const { getZendeskTicketGroups } = useIntegrationApi(api);

  return useQuery<ZendeskTicketGroupsResponse>({
    queryKey: "getZendeskTicketGroups",
    queryFn: getZendeskTicketGroups,
  });
};

export const useUpdateZendeskTicketGroups = () => {
  const { api } = useApi();
  const { workspace } = useWorkspace();
  const { updateZendeskTicketGroups } = useIntegrationApi(api);

  return useMutation({
    mutationFn: (
      groups: Array<{
        externalId: string;
        name: string;
        isIncludePolicy: boolean;
      }>
    ) => updateZendeskTicketGroups(groups, workspace),
  });
};

const nango = new Nango({
  publicKey: "7d3dd047-cdc9-4f26-ae76-a1a75565600a",
});

export function useCreateNangoIntegration({
  reloadIntegrations,
}: {
  reloadIntegrations: () => Promise<unknown>;
}) {
  const { workspace } = useWorkspace();
  const { api } = useApi();
  const integrationApi = useIntegrationApi(api);

  const { mutateAsync: createIntegration } = useMutation({
    mutationKey: ["createIntegration"],
    mutationFn: integrationApi.createIntegration,
  });

  const { mutateAsync: completeTicketSystemConfiguration } = useMutation({
    mutationKey: ["completeTicketSystemConfiguration"],
    mutationFn: integrationApi.completeTicketSystemConfiguration,
  });

  return useCallback(
    async (req: ConnectIntegrationRequest) => {
      try {
        const result = await nango.auth(req.name, workspace);

        const createIntegrationVariables = {
          ...req,
          isNangoAuth: true,
        };

        if (req.name === "confluence") {
          createIntegrationVariables.type = "WIKI";
        }

        if (req.name === "google_drive") {
          createIntegrationVariables.type = "WIKI";
        }

        await createIntegration(createIntegrationVariables);

        if (req.name === "monday") {
          await completeTicketSystemConfiguration({
            type: "TICKET_SYSTEM",
            name: "monday",
            groupsPolicy: { include: [], autoSync: true },
          });
        }

        await reloadIntegrations();
        toast(
          `Integration to ${
            integrationNameToGalleryItem[req.name].title
          } created successfuly`
        );
      } catch (error) {
        toast.error("Failed to create integration. Please try again.");
      }
    },
    [workspace]
  );
}

export const useCreateSmoochIntegration = ({
  reloadIntegrations,
}: {
  reloadIntegrations: () => Promise<unknown>;
}) => {
  const { api } = useApi();
  const { workspace } = useWorkspace();
  const { createSmoochIntegration } = useIntegrationApi(api);

  const { mutateAsync } = useMutation({
    mutationFn: createSmoochIntegration,
    onSuccess: () => {
      reloadIntegrations();
    },
    onError: () => {
      toast.error("Failed to create integration. Please try again.");
    },
  });

  return useCallback(
    async (params: { appId: string; keyId: string; secretKey: string }) => {
      await mutateAsync({
        params: {
          tenantId: workspace,
          type: "SMOOCH",
          ...params,
        },
      });
    },
    [mutateAsync, workspace]
  );
};

export const useUpdateIntegrationMetadata = () => {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const { updateIntegrationMetadata } = useIntegrationApi(api);

  return useMutation({
    mutationFn: (request: UpdateIntegrationMetadataRequest) =>
      updateIntegrationMetadata(request),
    onSuccess: () => {
      queryClient.invalidateQueries(["getTenantIntegrations"]);
    },
  });
};

export const useSyncKnowledgeAndWiki = () => {
  const { api } = useApi();
  const { syncKnowledgeAndWiki } = useIntegrationApi(api);

  return useMutation({
    mutationFn: () => syncKnowledgeAndWiki(),
    onSuccess: () => {
      toast.success("Knowledge and wiki resources syncing.");
    },
    onError: () => {
      toast.error("Failed to sync. Please try again.");
    },
  });
};
